'use client';
import { useState } from 'react';
import { useSite, useT } from '../../../lib/i18n';
import { pickName } from '../../../lib/i18n-core';
import type { ProductDetail } from '../../../lib/site';
import { BenefitNotices } from './BenefitNotice';
import { estimate, promotedTargets, targetLine, type CartLine } from './benefitEstimate';
import { BenefitPicks } from './BenefitPicks';
import { useRolling } from './useRolling';
import { Icon } from '../../Icon';
import { SmartLink } from '../../SmartLink';
import { toast } from '../../Toast';
import { WishButton } from '../WishButton';
import { platformLabel, badgeClass, typeLabel } from '../labels';
import { blockedLabel, buyable, discountShort, priceLines, statusLabel, won } from '../price';
import { cartChanged } from '../../cartCount';

/**
 * 교재 상세 상단 — 가이드 `book-detail.html` 의 `breadcrumb` + `bd-layout`(썸네일 / 정보).
 *
 * 강좌 상세(`PdTop` + `PdBuyBox`)와 **구성이 다르다** — 가이드가 그렇게 그려 뒀다.
 * 교재는 고를 것이 단가가 아니라 **수량**이고, 값·수량·버튼이 정보 칸 안에 이어 붙는다.
 *
 * **가이드에 있으나 그리지 않는 것** — 출판사·출판일·페이지 줄. 상품에 그런 칸이 없다
 * (현행 DB에도 없다). 없는 값을 지어내지 않고 있는 것만 적는다(ISBN·배송·재고).
 */
export function BdTop({ p, priceDetail, pointEnabled }: {
  p: ProductDetail;
  priceDetail: boolean;
  /** 마켓이 포인트를 쓰나 — 혜택 카드의 '함께쓰기' 문구가 이걸 본다 */
  pointEnabled: boolean;
}) {
  const t = useT();
  const { lang } = useSite();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  /*
   * 혜택 유도 — 조건이 이 교재만으로 안 맞을 때 같은 구성의 다른 대상을 함께 담게 한다
   * (2026-08-24 사용자 지시). 강좌 구매 박스와 같은 규칙·같은 도우미를 쓴다.
   */
  const benefits = p.benefits ?? [];
  const [withIds, setWithIds] = useState<Set<number>>(new Set());
  const toggleWith = (id: number) => setWithIds((sel) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const author = p.displayTeachers[0]?.teacher?.name;
  /** 배지는 **과목**이다(가이드의 이론·실무·법규) — 목록 카드와 같은 규칙으로 가장 깊은 분류 */
  const subject = p.categories.length
    ? pickName(p.categories[p.categories.length - 1].category.name, lang)
    : null;
  const money = t('prd.money');
  const discount = discountShort(p as never, money);
  const status = statusLabel(p as never, t);
  const canBuy = !status && buyable(p as never);

  /*
   * 살 수 있는 최대 수량 — **재고**와 **1회 구매 제한**(`max_avail_cnt`) 중 작은 쪽.
   * 제한 칸은 0 이 '제한 없음' 이다(현행 기본값). 가이드 입력칸의 max 99 를 상한으로 둔다.
   */
  const cap = Math.max(1, Math.min(
    p.bookMaxBuy && p.bookMaxBuy > 0 ? p.bookMaxBuy : 99,
    p.bookStock != null ? p.bookStock : 99,
    99,
  ));

  /** 체크한 관련 상품 — 같은 id 가 여러 구성에 있어도 한 번만 담는다 */
  const withTargets = (() => {
    const seen = new Map<number, { id: number; platform: string; price: number; isBook: boolean }>();
    for (const b of benefits) {
      for (const tg of b.targets) {
        if (tg.self || !withIds.has(tg.id) || seen.has(tg.id)) continue;
        // 그 혜택의 채널 단가로 담는다(강좌 구매 박스와 같은 규칙)
        const line = targetLine(tg, priceDetail, b.apply?.platform ?? null);
        seen.set(tg.id, { id: tg.id, platform: line.platform, price: line.price, isBook: tg.type === 'BOOK' });
      }
    }
    return [...seen.values()];
  })();

  const gross = p.price * qty + withTargets.reduce((a, x) => a + x.price, 0);
  // 교재는 수량이 있다 — 엔진도 교재 줄은 수량만큼 깎는다
  const cart: CartLine[] = [
    { productId: p.id, platform: 'ALL', price: p.price, quantity: qty, isBook: true },
    ...withTargets.map((x) => ({ productId: x.id, platform: x.platform, price: x.price, quantity: 1, isBook: x.isBook })),
  ];
  const est = estimate(benefits, cart);
  // 체크를 뺀 판정 — 체크한 순간 목록이 사라지지 않게(강좌 구매 박스와 같은 규칙)
  const baseEst = estimate(benefits, cart.filter((l) => !withTargets.some((x) => x.id === l.productId)));
  const sum = Math.max(0, gross - est.discount);
  const shownSum = useRolling(sum, { from: gross, level: est.discount });

  const addCart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 이 교재 + 혜택 조건을 채우려고 체크한 관련 상품
      const items = [
        { productId: p.id, quantity: qty },
        ...withTargets.map((x) => ({ productId: x.id, platform: x.platform })),
      ];
      let res = new Response(null, { status: 200 });
      for (const it of items) {
        res = await fetch('/api/members/me/cart', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(it),
        });
        if (!res.ok) break;
      }
      if (res.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast(body.message ?? t('prd.cartFailed'), 'error');
        return;
      }
      toast(t('prd.addedToCart'));
      cartChanged();
    } catch {
      toast(t('acc.err.network'), 'error');
    } finally { setBusy(false); }
  };

  return (
    <>
      <nav className="breadcrumb mb20">
        <SmartLink href="/">{t('nav.home')}</SmartLink>
        <Icon name="chevron-right" />
        <SmartLink href="/books">{t('nav.books')}</SmartLink>
        <Icon name="chevron-right" />
        <span>{p.name}</span>
      </nav>

      <div className="bd-layout mb30">
        <div className="bd-thumb-wrap">
          <div className="bd-thumb">
            {p.image
              ? <img src={p.image} alt={p.name} />
              : <div className="course-no-img"><Icon name="image" /></div>}
            {/* 찜 — 상품 상세는 이미지 우측 상단에 둔다(2026-08-23 사용자 지시. 강좌 상세와 같다) */}
            <WishButton productId={p.id} wished={p.wished} />
          </div>
        </div>

        <div className="bd-info">
          {/*
            배지는 **과목 하나**다(2026-08-23 사용자 지시로 정리). 개별구매·배송은 아래 메타
            칸에 줄로 적으므로 배지로 또 말하지 않는다 — 같은 값을 두 자리에서 말하지 않는다.
          */}
          {subject && (
            <div className="d-flex ai-center gap6 mb10">
              <span className="badge-type bg-blue filter">{subject}</span>
            </div>
          )}
          <h1 className="bd-title">{p.name}</h1>
          {author && <p className="bd-author"><Icon name="user" /> {t('prd.author', { name: author })}</p>}

          <div className="bd-meta-grid">
            {p.isbn && (
              <div className="bd-meta-row">
                <span className="bd-meta-label">{t('prd.book.isbn')}</span>
                <span className="bd-meta-val">{p.isbn}</span>
              </div>
            )}
            <div className="bd-meta-row">
              <span className="bd-meta-label">{t('prd.book.shipping')}</span>
              <span className="bd-meta-val">
                {p.bookFreeShipping ? t('prd.book.freeShipping') : t('prd.book.shippingPaid')}
              </span>
            </div>
            {/*
              구매 — 이 교재를 **혼자 살 수 있는지**(2026-08-23 사용자 지시).
              가능/불가를 늘 적는다 — 켜져 있을 때만 알리면 '불가'가 침묵과 구별되지 않는다.
            */}
            <div className="bd-meta-row">
              <span className="bd-meta-label">{t('prd.book.purchase')}</span>
              <span className="bd-meta-val">
                {t(p.bookSoloSale ? 'prd.book.soloSale' : 'prd.book.soloSaleNo')}
              </span>
            </div>
            {/* 재고는 **적을 때만** 알린다 — 넉넉한데 숫자를 적으면 없는 걱정을 만든다 */}
            {p.bookStock != null && p.bookStock <= 10 && (
              <div className="bd-meta-row">
                <span className="bd-meta-label">{t('prd.book.stock')}</span>
                <span className="bd-meta-val">{t('prd.book.stockLeft', { n: p.bookStock })}</span>
              </div>
            )}
          </div>

          {/*
            간략소개 카드 — **메타 칸 바로 아래**(2026-08-23 사용자 지시).
            강좌 상세의 긴 바(`pd-meta-row`)와 같은 모양을 쓴다 — 새 클래스를 짓지 않는다.
            `profile` 은 평문 255자다(리치 HTML 인 `contents` 는 교재소개 탭이 그린다).
            **없으면 아예 그리지 않는다** — 빈 칸이 남으면 덜 만든 화면처럼 보인다.
          */}
          {p.profile?.trim() && (
            <div className="pd-meta-row mb20">
              <div className="pd-meta-item">{p.profile}</div>
            </div>
          )}

          {/* 진행 중 혜택 — **가격·결제 칸 위**(강좌 상세와 같은 규칙) */}
          <BenefitNotices items={benefits} t={t} priceDetail={priceDetail} metKeys={est.activeKeys}
            product={{ coupon: p.couponEnabled, point: pointEnabled && p.pointEnabled }} marketPoint={pointEnabled} />
          {/* 교재 상세에는 묶음 칸이 없어 따로 선 카드로 낸다 */}
          <BenefitPicks picks={promotedTargets(benefits, baseEst.metKeys)} checked={withIds}
            onToggle={toggleWith} priceDetail={priceDetail} t={t} variant="card" />

          <div className="bd-price-box">
            {(p.priceOriginal > p.price || discount) && (
              <div className="d-flex ai-center gap10">
                {p.priceOriginal > p.price && <span className="bd-price-origin">{won(p.priceOriginal)}{money}</span>}
                {discount && <span className="badge badge-danger badge-pill">{discount}</span>}
              </div>
            )}
            <span className="bd-price">{p.price <= 0 ? t('prd.free') : `${won(p.price)}${money}`}</span>
          </div>

          {canBuy && (
            <>
              <div className="bd-qty-row">
                <div className="bd-qty">
                  <button className="bd-qty-btn" type="button" onClick={() => setQty((n) => Math.max(1, n - 1))}
                    aria-label={t('prd.qtyDown')}><Icon name="minus" /></button>
                  <input type="number" className="bd-qty-input" value={qty} min={1} max={cap} readOnly />
                  <button className="bd-qty-btn" type="button" onClick={() => setQty((n) => Math.min(cap, n + 1))}
                    aria-label={t('prd.qtyUp')}><Icon name="plus" /></button>
                </div>
                <span className="bd-qty-total">
                  {t('prd.sum')} <strong className={est.discount > 0 ? 'is-cut' : undefined}>
                    {won(shownSum)}{money}
                  </strong>
                </span>
              </div>

              {/* 가이드는 이 화면에서 두 버튼에 아이콘을 함께 둔다(강좌 상세는 글자만이다) */}
              <div className="bd-btn-group">
                <button className="action-btn action-cancel" disabled={busy} onClick={() => void addCart()}>
                  <Icon name="shopping-cart" /> {t('prd.cart')}
                </button>
                {/*
                  바로구매 — **장바구니를 거치지 않고 결제 화면으로**(2026-08-26 사용자 제보:
                  장바구니로 갔다). 강좌 상세와 같은 길이다 — 지금 화면에서 고른 것을 그대로
                  넘긴다(수량, 혜택 조건을 채우려고 체크한 관련 상품까지).
                */}
                <SmartLink className="action-btn action-submit bd-buy-btn"
                  href={`/checkout?buy=${cart.map((c) => `${c.productId}:${c.quantity}:${c.platform}`).join(',')}`}>
                  <Icon name="credit-card" /> {t('prd.buyDirect')}
                </SmartLink>
              </div>
            </>
          )}

          {!canBuy && (
            <div className="bd-btn-group">
              <button className="action-btn action-cancel" disabled>
                {status?.text ?? blockedLabel(p as never, t) ?? t('prd.notBuyable')}
              </button>
            </div>
          )}

          {/*
            관련 강의 — 이 교재를 끼워 파는 강좌(가이드 `bd-rel-course`).
            없으면 칸 자체를 그리지 않는다. 아이콘 이름은 lucide 가 개명해 `circle-play` 다
            (가이드의 `play-circle` 은 옛 이름 — 그대로 쓰면 조용히 사라진다).
          */}
          {p.relatedLectures.length > 0 && (
            <div className="bd-rel-course mt20">
              <p className="bd-rel-label"><Icon name="circle-play" /> {t('prd.relCourse')}</p>
              {p.relatedLectures.map((c) => (
                <SmartLink key={c.id} href={`/lectures/${c.id}`} className="bd-rel-item">
                  <span className={`badge-type ${badgeClass(c.type)} filter`} style={{ flexShrink: 0 }}>
                    {typeLabel(c.type, t)}
                  </span>
                  <span className="bd-rel-title">{c.name}</span>
                  {/*
                    값은 **파는 단가를 모두** 적는다(2026-08-26 사용자 제보) — 공용 단가만
                    찍으면 PC·모바일로만 파는 강좌가 `무료` 로 보인다. 목록·상세와 같은 규칙
                    (`priceLines`)이라 세 화면이 갈리지 않는다. 세분화 마켓은 어느 단가인지도 적는다.
                  */}
                  <span className="bd-rel-price">
                    {(() => {
                      const lines = priceLines(c as never, priceDetail).filter((l) => l.price > 0);
                      if (!lines.length) return t('prd.free');
                      return lines.map((l) => (
                        <span key={l.platform} className="bd-rel-price-item">
                          {priceDetail && <em>{platformLabel(l.platform, t)}</em>}
                          {won(l.price)}{money}
                        </span>
                      ));
                    })()}
                  </span>
                </SmartLink>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
